/*
    Merrid Controls. Wszelkie prawa zastrzeżone.
    Plik zawiera kody Źródłowe będące własnością firmy Merrid Controls.
    Nie wolno ich rozpowszechniać w całości lub fragmentach bez wyraźnej zgody właściciela.
*/

/** Import ENVIRONMENTAL VARIABLES */
import ENV from "react-dotenv";

/** Import Main React Library Object */
import React, {useEffect} from "react";

/** Import Redux Hook - Getter for Data from Application State Store Object */
import useTypedSelector from "@Services/Hooks/UseTypedSelector";

import {useDispatch} from "react-redux";

import CHANGE_MODAL_MESSAGE_ACTION from "@Actions/Interface/ChangeModalMessage";


/** Import Theme Context - Information for colors, etc... */
import ApplicationThemeContext, {APPLICATION_THEME_SETTINGS} from "@Contexts/Theme";
/** Import Router Context - Routes URLs List */
import ApplicationRouterContext, {APPLICATION_ROUTER} from "@Contexts/Router";

/** Import User Interface - Logged User Object Type */
import LOGGED_USER_INTERFACE from "@Store/Interfaces/Authentication/User";


/** Import Helmet Module - Managing Head of the document (like title, etc...) */
import {Helmet} from "react-helmet";

/** Import Router Module - HOC for Application Routing */
import {BrowserRouter as Router} from "react-router-dom";

/** Import React Bootstrap Components */
import {Container} from "react-bootstrap";


/** Import Component Children Components */
import RestApiRequestLoaderComponent from "@Components/UI/Loaders/ApiRequest"; // REST API Request Loader COmponent

import ApplicationComponent from "@Components";
import ModalMessageComponent from "@Components/UI/Modals/Message/Default";


/** @type ApplicationProps - Props List for Main Application Component */
type ApplicationProps = {};


/**
 * @visibleName Main Application High Order Component
 *
 * @return JSX.Element
 */
export const Application: React.FC<ApplicationProps> = (): JSX.Element => {

    const DISPATCHER = useDispatch();

    /** @const {object} INTERFACE_DATA - Application State Store Interface Data */
    const INTERFACE_DATA:ReturnType<any> = useTypedSelector(
        (state) => state.interface
    );

    /** @const {boolean} REST_API_REQUEST_STATUS - Application Global State Rest Api Request Status */
    const REST_API_REQUEST_STATUS: boolean = INTERFACE_DATA?.apiRequestStatus || false;

    const MODAL_MESSAGE = INTERFACE_DATA?.modalMessage || null;

    /** @const {UserInterface | null} LOGGED_USER - Application Global State Logged User Object */
    const LOGGED_USER: LOGGED_USER_INTERFACE | null = useTypedSelector(
        (state):ReturnType<any> => state?.authentication
    )?.loggedUser || null;


    /**
     * @description render Component Content
     * @return JSX.Element
     */
    return (
        // Theme Context - HOC for Theme Data
        <ApplicationThemeContext.Provider value={APPLICATION_THEME_SETTINGS}>
            {/* Router Context - HOC for Routes URLs List */}
            <ApplicationRouterContext.Provider value={APPLICATION_ROUTER}>
                {/* React Router DOM Component - Application Routing System */}
                <Router>

                    {/* Helmet Module Component - Set Document Title */}
                    <Helmet>
                        <title>{ENV?.APP_TITLE || ""}</title>
                    </Helmet>
                    {/* /Helmet Module Component - Set Document Title */}

                    {!!MODAL_MESSAGE &&
	                <ModalMessageComponent
		                message={MODAL_MESSAGE.content} type={MODAL_MESSAGE.type}
                        openStatus={true} closeHandler={() => DISPATCHER(CHANGE_MODAL_MESSAGE_ACTION(null))}
                        acceptHandler={MODAL_MESSAGE.acceptHandler} declineHandler={MODAL_MESSAGE.declineHandler}
	                />
                    }

                    {/* REST API REQUEST LOADER COMPONENT */}
                    {!!REST_API_REQUEST_STATUS && <RestApiRequestLoaderComponent />}
                    {/* /REST API REQUEST LOADER COMPONENT */}

                    {/* Main Application DOM Container Element */}
                    <Container fluid={true} className={"main-application-container" + (LOGGED_USER?.userID ? " authenticated-user" : "")}>
                        {/* Application Main Content Component */}
                        <ApplicationComponent />
                    </Container>
                    {/* /Main Application DOM Container Element */}

                </Router>
                {/* /React Router DOM Component - Application Routing System */}
            </ApplicationRouterContext.Provider>
            {/* /Router Context - HOC for Routes URLs List */}
        </ApplicationThemeContext.Provider>
        // Theme Context - HOC for Theme Data
    );

};


/** @export {React.FC} Application - Main HO Application Component */
export default Application;
